import { RUNTIME_BACKEND_URL } from '../lib/backendUrl';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const API = `${RUNTIME_BACKEND_URL}/api`;

// JWT exp claim'ini decode edip token'ın geçerli olup olmadığını kontrol eder.
// Sunucuda 7 gün expire var; client bu süreyi bildiğinde otomatik logout eder.
export function isTokenValid(token) {
  if (!token || typeof token !== 'string') return false;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return false;
    // base64url → base64
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64.length % 4 === 0 ? '' : '='.repeat(4 - (b64.length % 4));
    const json = atob(b64 + pad);
    const payload = JSON.parse(json);
    if (!payload.exp) return true; // exp yoksa sunucuya bırak
    // exp saniye cinsinden; şu anki zamana ~60sn margin ekle
    const nowSec = Math.floor(Date.now() / 1000);
    return payload.exp > nowSec + 60;
  } catch {
    return false;
  }
}

export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      setUser: (user, token) => set({ user, token, isAuthenticated: true }),

      // Token geçerliliğini kontrol et; expire olduysa otomatik logout
      checkAuthValid: () => {
        const { token, isAuthenticated } = get();
        if (isAuthenticated && !isTokenValid(token)) {
          set({ user: null, token: null, isAuthenticated: false, error: 'Oturumunuzun süresi doldu, lütfen tekrar giriş yapın' });
          return false;
        }
        return isAuthenticated;
      },

      login: async (email, password) => {
        set({ isLoading: true, error: null });
        try {
          const response = await fetch(`${API}/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password })
          });

          if (!response.ok) {
            const data = await response.json();
            throw new Error(data.detail || 'Giriş başarısız');
          }

          const data = await response.json();
          set({
            user: data.user,
            token: data.access_token,
            isAuthenticated: true,
            isLoading: false
          });
          return data.user;
        } catch (error) {
          set({ error: error.message, isLoading: false });
          throw error;
        }
      },

      loginWithGoogle: async (credential) => {
        set({ isLoading: true, error: null });
        try {
          const response = await fetch(`${API}/auth/google`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ credential })
          });
          const data = await response.json();
          if (!response.ok) throw new Error(data.detail || 'Google ile giriş başarısız');
          set({ user: data.user, token: data.access_token, isAuthenticated: true, isLoading: false });
          return data.user;
        } catch (error) {
          set({ error: error.message, isLoading: false });
          throw error;
        }
      },

      register: async (userData) => {
        set({ isLoading: true, error: null });
        try {
          const response = await fetch(`${API}/auth/register`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(userData)
          });

          if (!response.ok) {
            const data = await response.json();
            throw new Error(data.detail || 'Kayıt başarısız');
          }

          const data = await response.json();

          if (data.status === 'pending_approval') {
            set({ isLoading: false });
            return data;
          }

          set({
            user: data.user,
            token: data.access_token,
            isAuthenticated: true,
            isLoading: false
          });
          return data;
        } catch (error) {
          set({ error: error.message, isLoading: false });
          throw error;
        }
      },

      logout: () => {
        set({ user: null, token: null, isAuthenticated: false, error: null });
      },

      updateTheme: async (theme) => {
        const { token, user } = get();
        try {
          const response = await fetch(`${API}/auth/theme`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ theme })
          });

          if (response.ok) {
            set({ user: { ...user, theme } });
          }
        } catch (error) {
          console.error('Theme update failed:', error);
        }
      },

      updateUser: (updatedUser) => {
        const { user } = get();
        set({ user: { ...user, ...updatedUser } });
      }
    }),
    {
      name: 'wedding-takip-auth',
      partialize: (state) => ({ user: state.user, token: state.token, isAuthenticated: state.isAuthenticated }),
      // localStorage'dan yüklenirken token'ı doğrula; expire olduysa temizle
      onRehydrateStorage: () => (state) => {
        if (state && state.token && !isTokenValid(state.token)) {
          state.user = null;
          state.token = null;
          state.isAuthenticated = false;
          state.error = 'Oturumunuzun süresi doldu, lütfen tekrar giriş yapın';
        }
      }
    }
  )
);
